import React, { useRef, useEffect, useCallback } from 'react';

// Minimal profile data interface
interface ProfileData {
  id: string;
  name: string;
  title: string;
  experience: string;
  imgUrl: string;
}

// Static profiles with only the necessary fields
const staticProfiles: ProfileData[] = [
  { id: '1',  name: 'Alven P. 🇦🇴',       title: 'Design Strategist', experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/675718ced3aed35d4941894d_663b3ed5e3bbdae2453ab55f_6638eba985de95b75d72183a_Alven_Paulo.jpeg' },
  { id: '2',  name: 'Wilson L. 🇨🇻',      title: 'Graphic Designer',  experience: '+3 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/66446e1b381b2b6871d8041d_Wilson_Lima.jpg' },
  { id: '3',  name: 'Jennifer B. 🇨🇻',    title: 'UI/UX Designer',    experience: '+11 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/66263aab07b4e071217691d0_Jennifer_Brazao.jpg' },
  { id: '4',  name: 'Sidney. T 🇲🇿',      title: 'Creative Director', experience: '+4 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/661fd1ac52342b15facf0644_Sidney_T._Taju.jpg' },
  { id: '5',  name: 'Hassan A. 🇪🇬',      title: 'Game Designer',     experience: '+4 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/656463d4de4498f2fd4c6bcb_Hassan%20Arafat.jpg' },
  { id: '6',  name: 'Lisiane F. 🇨🇻',     title: 'Graphic Designer',  experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/6618498a1975beaca4435387_Lisiane_Fernandes.jpg' },
  { id: '8',  name: 'Jade O. 🇿🇦',        title: 'Content Designer',  experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/6560a92ff89486184fd18e15_Jade%20(Sasseen)%20Ottavini%202.jpeg' },
  { id: '9',  name: 'Adrian J. 🇰🇪',      title: 'Art Director',      experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/657c3bf419daf496a80505e8_Adrian_Jankowiak.jpg' },
  { id: '10', name: 'Gabriela P. 🇲🇿',    title: 'Game Designer',     experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/675804152b143e8a32264cfe_661540ceb0fde31e0c8704cf_Gabriela_Paul-p-500.jpg' },
  { id: '11', name: 'Amine S. 🇲🇦',       title: 'UX Designer',       experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/6560b0a282c9d6d1707d097b_Amine%20Seraj%202.jpeg' },
  { id: '12', name: 'Jokotade A.  🇳🇬',    title: 'Product Designer',  experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/666aa593624401fe7d86c276_Jokotade_Adewale.jpg' },
  { id: '13', name: 'Alcides E. 🇦🇴',     title: 'Graphic Designer',  experience: '+7 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/665ecb2948dc6342f36fdcd1_Alcides_Epalanga.jpg' },
  { id: '14', name: 'Ukany F. 🇦🇴',       title: 'Creative Director', experience: '+4 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/66170970c86b1c9f16348e3d_Ukany_Filipe.jpg' },
  { id: '15', name: 'Joaquim V. 🇦🇴',       title: 'Visual Designer', experience: '+5 years of experience', imgUrl: 'https://cdn.prod.website-files.com/60ff31ec144098c80dbc6ef2/6617047cbba646e99d06a668_Joaquim_Ventura.jpg' },
];

// Custom card component
const CustomProfileCard: React.FC<ProfileData> = ({ name, title, experience, imgUrl }) => (
  <div className="bg-white shadow-[0_0_12px_rgba(25,27,31,0.08)] border-[1.5px] border-solid border-[#e4e7eb] rounded-[8.95px] flex flex-col items-start">
    <div className='relative'>
      <img src={imgUrl} alt={name} />
    </div>
    <div className='p-4'>
      <div className='flex gap-2 items-center mb-1'>
        <h3 className="text-sm font-bold text-[#505050] ">{name}</h3>
        <img width="14" height="14"   src="https://cdn.prod.website-files.com/60ff31ec144098c6d8bc6ed2/66154fe2ee93ca9148a133c5_verified_1.svg" alt="verification badge" />
      </div>

      <div className='flex gap-2 items-center mb-2'>
        <img width="14" height="14" src="https://cdn.prod.website-files.com/60ff31ec144098c6d8bc6ed2/6558c0d091ac8fe073e011ab_fi-rr-briefcase.svg" alt="role icon" />
        <p className="text-xs text-[#505050]">{title}</p>
      </div>

      <p className="text-xs text-[#505050]">{experience}</p>
    </div>
  </div>
);

const InfiniteScrollProfiles: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Animation refs
  const animationRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);
  const scrollSpeedRef = useRef(0.3);

  // Clone content for infinite loop
  const cloneContent = useCallback(() => {
    if (!contentRef.current || !scrollContainerRef.current) return;
    // remove old clones
    while (contentRef.current.children.length > staticProfiles.length) {
      contentRef.current.removeChild(contentRef.current.lastChild!);
    }
    // append fresh clones
    Array.from(contentRef.current.children).forEach(child => {
      contentRef.current!.appendChild(child.cloneNode(true));
    });
    scrollPositionRef.current = 0;
    scrollContainerRef.current.scrollLeft = 0;
  }, []);

  // Animate scrolling
  const animateScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    scrollPositionRef.current += scrollSpeedRef.current;
    container.scrollLeft = scrollPositionRef.current;
    const half = (contentRef.current?.scrollWidth || 0) / 2;
    if (container.scrollLeft >= half) {
      scrollPositionRef.current = 0;
      container.scrollLeft = 0;
    }
    animationRef.current = requestAnimationFrame(animateScroll);
  }, []);

  // Initialize
  useEffect(() => {
    cloneContent();
    animationRef.current = requestAnimationFrame(animateScroll);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [cloneContent, animateScroll]);

  return (
    <div
      ref={scrollContainerRef}
      className="w-full max-w-screen- overflow-hidden mx-auto"
      style={{
        maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, black 5%, black 95%, rgba(0,0,0,0) 100%)',
        WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, black 5%, black 95%, rgba(0,0,0,0) 100%)'
      }}
    >
      <div ref={contentRef} className="flex flex-nowrap gap-4">
        {staticProfiles.map(p => (
          <div key={p.id} className="w-[200px] flex-shrink-0">
            <CustomProfileCard {...p} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default InfiniteScrollProfiles;
